import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  faCircleInfo,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { faExternalLinkSquare } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  createBemBlockBuilder,
  Hyperlink,
  InPageNotification,
} from '@ebsco-ui/ebsco-ui';

import { Icon, Modal, ModalHeader, Notification } from '@app/components';
import { sharedMessages } from '@app/translations';

import { DownloadFormat } from '../../../constants';
import { isMarcFormat } from '../utils';

interface DownloadMarcModalProps {
  isOpen: boolean;
  formats: DownloadFormat[];
  onClose: () => void;
}

const cnBem = createBemBlockBuilder(['downloadMarc']);

export const DownloadMarcModal: FC<DownloadMarcModalProps> = ({
  isOpen,
  formats,
  onClose,
}) => {
  const { $t } = useIntl();
  const [isDownloadError] = useState(false);
  const [isDownloadInProgress] = useState(false);

  const modalTitle = $t({
    id: 'instanceDetails.downloadCatalogingRecord',
    defaultMessage: 'Download cataloging record',
  });

  const renderNotification = () => {
    if (isDownloadError) {
      return (
        <Notification
          variant={InPageNotification.VARIANT.error}
          icon={faExclamationTriangle}
          className={cnBem('__notification')}
          header={$t({
            id: 'instanceDetails.unableToDownload',
            defaultMessage: 'Unable to download record. Please try again.',
          })}
        />
      );
    }

    if (isDownloadInProgress) {
      return (
        <Notification
          variant={InPageNotification.VARIANT.information}
          icon={faCircleInfo}
          className={cnBem('__notification')}
          header={$t({
            id: 'instanceDetails.yourDownloadWillCompleteShortly',
            defaultMessage: 'Your download will complete shortly.',
          })}
        />
      );
    }

    return null;
  };

  const renderModalBody = () => (
    <Modal.Body className={cnBem('__modalBody')}>
      {renderNotification()}
      <ul>
        {formats.map(({ name, link }) => (
          <li key={name}>
            {isMarcFormat(name) ? (
              <Button styleType="link" className="underlinedLink">
                {name}
              </Button>
            ) : (
              <>
                <Hyperlink href={link} target="_blank">
                  {name}
                </Hyperlink>
                <Icon icon={faExternalLinkSquare} />
              </>
            )}
          </li>
        ))}
      </ul>
    </Modal.Body>
  );

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={modalTitle}
      className={cnBem()}
      onClose={onClose}
    >
      <ModalHeader
        title={modalTitle}
        className={cnBem('__modalHeader')}
        onClose={onClose}
      />
      {renderModalBody()}
      <Modal.Footer>
        <Button styleType="text" onClick={onClose}>
          {$t(sharedMessages.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
